import type React from "react";
import { Linking } from "react-native";
import { useLink } from "solito/link";
import { isWeb, styled } from "tamagui";
import type { IconProps } from "@tamagui/helpers-icon";
import { ChevronRight } from "@tamagui/lucide-icons";
import { settingPreviewUrl } from "@medbillai/utils";
import { XStack, YStack, type YStackProps } from "./Stacks";
import { Body1, Body2 } from "./Typography";
export type SettingItemProps = YStackProps & {
  icon?: React.FC<IconProps>;
  leadingIcon?: React.FC<IconProps>;
  subtitle?: React.ReactNode;
  customElement?: React.ReactNode;
  externalUrl?: {
    title: string;
    url: string;
  };
  isActive?: boolean;
  secondaryItem?: boolean;
  /** Render the item in a destructive/negative tone. */
  error?: boolean;
};
export const SettingItem = ({
  icon: Icon,
  leadingIcon: LeadingIcon,
  subtitle,
  customElement: CustomElement,
  children,
  isActive,
  externalUrl,
  secondaryItem,
  error,
  ...props
}: SettingItemProps) => {
  let externalLinkProps = {};
  const linkProps = useLink({
    href: settingPreviewUrl({
      title: externalUrl?.title ?? "",
      uri: externalUrl?.url ?? ""
    })
  });
  if (externalUrl) {
    if (isWeb) {
      externalLinkProps = {
        onPress: () => Linking.openURL(externalUrl.url)
      };
    } else {
      externalLinkProps = {
        ...linkProps
      };
    }
  }
  const iconColor = error ? "$iconNegative" : secondaryItem ? "$iconSubtle" : "$iconDefault";
  const textColor = error ? "$textNegative" : "$textDefault";
  return <SettingItemFrame isActive={!!isActive} error={!!error} {...externalLinkProps} {...props} data-sentry-element="SettingItemFrame" data-sentry-component="SettingItem" data-sentry-source-file="SettingItem.tsx">
      {LeadingIcon ? <LeadingIcon color={iconColor} size={secondaryItem ? 14 : 18} /> : null}
      {secondaryItem ? <Body2 f={1} color="$textSubtle">
          {children}
        </Body2> : subtitle ? <YStack f={1} gap="$0.5" minWidth={0}>
          <Body1 color={textColor} flexShrink={0} numberOfLines={1}>
            {children}
          </Body1>
          <Body2 color="$textSubtle" ellipsizeMode="tail" flexShrink={1} minWidth={0} numberOfLines={1}>
            {subtitle}
          </Body2>
        </YStack> : <Body1 f={1} color={textColor}>
          {children}
        </Body1>}
      {Icon ? <Icon size={secondaryItem ? 14 : 18} color={iconColor} /> : !isWeb && !CustomElement ? <ChevronRight color={iconColor} size={secondaryItem ? 14 : 18} /> : null}
      {CustomElement ?? null}
    </SettingItemFrame>;
};
const SettingItemFrame = styled(XStack, {
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  gap: "$4",
  borderRadius: "$4",
  "$platform-web": {
    paddingHorizontal: "$sp2",
    paddingVertical: "$sp1.5",
    borderRadius: "$radius1"
  },
  "$platform-native": {
    minHeight: "$4",
    px: "$3",
    py: "$0"
  },
  variants: {
    isActive: {
      true: {
        bg: "$surface0",
        "$platform-web": {}
      },
      false: {
        hoverStyle: {
          bg: "$surface1Hover"
          // No hover effect on mobile
        },
        pressStyle: {
          bg: "$surface1Hover"
          // Press effect in variant with media query doesn't work so no border
          // effect on press (is not visible regardless)
        }
      }
    },
    error: {
      true: {
        hoverStyle: {
          bg: "$bgNegative"
        },
        pressStyle: {
          bg: "$bgNegative"
        }
      },
      false: {}
    }
  } as const
});